import { useDispatch } from "react-redux";
import { Button, Result, Typography } from "antd";
import { performLogout } from "../utils/performLogout";

/**
 * Full-screen state for a signed-in user whose profile request failed.
 *
 * The Firebase session is intact, so the user is kept signed in and offered
 * the two actions that can change the state: re-run the fetch, or sign out
 * through the shared teardown. A redirect to /login would show a form that
 * cannot fix anything, since the sign-in already succeeded. The copy names a
 * temporary connection problem because that is the usual cause, and the raw
 * failure text sits underneath for support.
 *
 * `ProtectedRoute` owns the retry: it keeps this screen mounted while the
 * fetch is in flight, so the previous profile never renders the app between
 * a click and its answer.
 *
 * @param {{ error: string | null, retrying: boolean, onRetry: () => void }} props
 */
const ProfileLoadFailed = ({ error, retrying, onRetry }) => {
  const dispatch = useDispatch();

  return (
    <Result
      status="warning"
      title="Having trouble connecting"
      subTitle={
        <>
          <p>
            You&apos;re signed in, but we couldn&apos;t load your workspace just
            now. This is usually a temporary connection issue. Try again in a
            moment, or sign out and sign back in.
          </p>
          {error && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Details: {error}
            </Typography.Text>
          )}
        </>
      }
      extra={[
        <Button key="retry" type="primary" loading={retrying} onClick={onRetry}>
          Try again
        </Button>,
        <Button
          key="signout"
          disabled={retrying}
          onClick={() => performLogout(dispatch)}
        >
          Sign out
        </Button>,
      ]}
    />
  );
};

export default ProfileLoadFailed;
